/**
 * Copyright (C) Radiojar S.A. - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 *
 * Created by Tasos Kolokathis on 24/3/25.
 */

import store from "@/store";
import moment from "moment";
import axios from "axios";
import WorkerTimer from "@/plugins/WorkerTimer";
import {jwtDecode} from "jwt-decode"

let tokenExpirationTimeout = null;
const authConfig = store.state.authConfig.auth;

export default {
	async InitSession() {
		await handleLoginPromise();

		const user = JSON.parse(localStorage.getItem("user.session"));
		if (user) {
			if (tokenExpired(user)) {
				await getAccessToken(user)
					.then(() => {
						window.history.pushState({}, "", "/");
					})
					.catch(onError);
			}
			else {
				onAccessTokenReceived(user);
			}
		}

		axios.interceptors.response.use(
			response => response,
			async error => {
				if (error?.response?.status === 401) {
					console.log("Request new access token");
					const user = JSON.parse(localStorage.getItem("user.session"));
					await getAccessToken(user);
					error.config.headers["Authorization"] = store.getters.getAccessToken();
					console.log("Retry the request using the new access token");
					return axios.request(error.config);
				}
				return Promise.reject(error);
			}
		);
	},

	async SignIn(userName) {
		localStorage.setItem("login_callback_qs", window.location.search);

		const params = {
			userName,
			returnUrl: `${window.location.origin}${authConfig.redirectUri}`
		};
		const qs = new URLSearchParams(params);
		window.location.replace(`${authConfig.authority}v1/authentication/login?${qs.toString()}`);
	},

	async SignOut(inactivity) {
		localStorage.removeItem("user.session");

		if (!inactivity) {	//todo: Temporary solution. Disable logout process for inactivity reason, since the RCS IAM doesn't work properly
			const params = {
				accessToken: store.getters.getAccessToken().replace("Bearer ", ""),
				returnUrl: window.location.origin
			};
			const qs = new URLSearchParams(params);

			window.location.replace(`${authConfig.authority}v1/authentication/logout?${qs.toString()}`);
		}
		else {
			window.location.href = "/";
		}

		// await axios({
		// 		method: "GET",
		// 		url: `${authConfig.authority}v1/authentication/logout`,
		// 		params: {
		// 			accessToken: store.getters.getAccessToken().replace("Bearer ", ""),
		// 			returnUrl: window.location.origin
		// 		},
		// 		headers: {
		// 			"Authorization": store.getters.getAccessToken()
		// 		}
		// 	}
		// )
		// 	.then(() => {
		// 		localStorage.removeItem("user.session");
		// 		store.commit("setAccessToken", null);
		// 	})
		// 	.catch((error) => {
		// 		console.log(error);
		// 		// onError
		// 	});
	}
};

const handleLoginPromise = () => {
	if (window.location.pathname === authConfig.redirectUri) {
		const params = new URLSearchParams(window.location.search);

		const {AccessToken, RefreshToken} = Object.fromEntries(params.entries());
		const {jti, exp} = jwtDecode(AccessToken);

		return axios({
				method: "GET",
				url: `${authConfig.authority}v1/tokens/${jti}`,
				headers: {
					"Authorization": `Bearer ${AccessToken}`
				}
			}
		)
			.then(response => response.data.content)
			.then(response => {
				const user = {
					access_token: AccessToken,
					refresh_token: RefreshToken,
					profile: response.currentUser.user,
					organizationId: response.organizationId,
					expires_at: moment.unix(exp).valueOf()
				};

				store.commit("setAvailableOrganizations", response.organizationIds);

				onAccessTokenReceived(user);
				window.history.pushState({}, "", `/${localStorage.getItem("login_callback_qs") ?? ""}`);
				localStorage.removeItem("login_callback_qs");
			})
			.catch(onError);
	}

	return false;
}

const tokenExpired = (user) => {
	const now = moment();
	const expiration = moment(user.expires_at).subtract(2, "minutes");	// 2 minutes earlier
	return expiration.isSameOrBefore(now);
};

const waitForRefreshToken = (user) => {
	const now = moment();
	const expiration = moment(user.expires_at).subtract(2, "minutes");	// 2 minutes earlier

	if (tokenExpirationTimeout) {
		WorkerTimer.clearTimeout(tokenExpirationTimeout);
		tokenExpirationTimeout = null;
	}

	console.log(`Token expires in ${moment(user.expires_at).diff(now, "minutes")} minutes.`);

	tokenExpirationTimeout = WorkerTimer.setTimeout(async () => {
		console.log("Authorization token is about to expire.");
		await getAccessToken(user);
	}, expiration.diff(now));
}

export const getAccessToken = (user, newOrgID) => {
	const data = {
		accessToken: user.access_token,
		refreshToken: user.refresh_token,
		organizationId: newOrgID ?? user.organizationId
	};

	return axios({
			method: "GET",
			url: `${authConfig.authority}v1/authentication/refreshtoken`,
			params: data,
			headers: {
				"Authorization": `Bearer ${user.access_token}`,
			}
		}
	)
		.then(response => response.data)
		.then(session => {
			const {exp} = jwtDecode(session.accessToken);

			user = {
				...user,
				expires_at: moment.unix(exp).valueOf(),
				access_token: session.accessToken,
				refresh_token: session.refreshToken
			};

			if (newOrgID) {
				user.organizationId = newOrgID;
				user.profile.lastOrganizationId = newOrgID;
			}
			onAccessTokenReceived(user);
		})
		.catch(onError);
}

const onAccessTokenReceived = (user) => {
	localStorage.setItem("user.session", JSON.stringify(user));
	store.commit("setAccessToken", user.access_token);
	store.commit("setAuthAccount", formatAccount(user));
	store.dispatch("refreshWebsocketConnection");

	waitForRefreshToken(user);
}

const onError = (error) => {
	console.log(error);
	store.commit(
		"SHOW_SUCCESS_NOTIFICATION",
		{message: "Something went wrong. You will be redirected to login page."}
	);

	setTimeout(() => {
		localStorage.removeItem("user.session");
		window.location.href = "/";
	}, 3000);
}

const formatAccount = (user) => {
	return {
		userId: user.profile.id,
		email: user.profile.email,
		firstName: user.profile.firstName,
		lastName: user.profile.lastName,
		fullName: `${user.profile.firstName} ${user.profile.lastName}`.trim(),
		organizationId: user.organizationId
	}
}